import type { LocationQuery } from 'vue-router';
type RouteWithQuery = { query: LocationQuery };

const getFirstQueryValue = ({
  queryKey,
  route,
}: {
  queryKey: string;
  route: RouteWithQuery;
}): string | null => {
  const queryValue = route.query[queryKey];
  if (Array.isArray(queryValue)) {
    const firstValue = queryValue[0];
    return typeof firstValue === 'string' ? firstValue : null;
  }

  if (typeof queryValue === 'string') {
    return queryValue;
  }

  return null;
};

export const getStringQueryParameter = ({
  queryKey,
  route,
}: {
  queryKey: string;
  route: RouteWithQuery;
}): string | null => {
  const rawValue = getFirstQueryValue({ queryKey, route });
  if (!rawValue) {
    return null;
  }

  const trimmedValue = rawValue.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
};

export const getBooleanQueryParameter = ({
  queryKey,
  route,
}: {
  queryKey: string;
  route: RouteWithQuery;
}): boolean | null => {
  const value = getStringQueryParameter({ queryKey, route });
  if (!value) {
    return null;
  }

  const normalizedValue = value.toLowerCase();
  if (normalizedValue === 'true') {
    return true;
  }

  if (normalizedValue === 'false') {
    return false;
  }

  return null;
};

export const getIntegerQueryParameter = ({
  queryKey,
  route,
  minValue,
}: {
  queryKey: string;
  route: RouteWithQuery;
  minValue?: number;
}): number | null => {
  const value = getStringQueryParameter({ queryKey, route });
  if (!value || !/^-?\d+$/.test(value)) {
    return null;
  }

  const parsedValue = Number.parseInt(value, 10);
  const effectiveMinValue = minValue ?? Number.NEGATIVE_INFINITY;
  if (!Number.isFinite(parsedValue) || parsedValue < effectiveMinValue) {
    return null;
  }

  return parsedValue;
};
